import type { JSX } from 'react';
import { Alert, Anchor, Badge, Button, Center, Group, List, Loader, Stack, Table, Text } from '@mantine/core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, offerFilesApi, type Offer, type OfferPackage } from '../../api';
import { formatBytes, formatDateTime } from '../../format';
import { Section } from '../../ui/Section';

/**
 * The files of an offer (AP-93, AP-94): made from the office's templates
 * (Einstellungen › Vorlagen) for every variant, recalculated and checked,
 * as one package with a folder per variant and addressee.
 */
export function OfferFiles({ offer }: { offer: Offer }): JSX.Element {
  const queryClient = useQueryClient();
  const list = useQuery({ queryKey: ['offer-files', offer.id], queryFn: () => offerFilesApi.list(offer.id) });
  const make = useMutation({ mutationFn: () => offerFilesApi.generate(offer.id), onSettled: () => queryClient.invalidateQueries({ queryKey: ['offer-files', offer.id] }) });
  const latest = list.data?.[0];
  return (
    <Stack gap="md">
      <Section
        title="Angebotsunterlagen"
        description={`Aus den Vorlagen des Büros, für ${offer.variants.length === 1 ? 'die Variante' : `alle ${offer.variants.length} Varianten`}: Kalkulation (intern), Preisblatt und Angebot (an den Auftraggeber), Rückfragen (an die ausschreibende Stelle), Risikomatrix und Arbeitspapier (intern)${offer.variants.length > 1 ? ', Entscheidungsvorlage' : ''}. Dateinamen nach dem Muster JJMMTT-${offer.code}-Inhalt.`}
        actions={<Button onClick={() => make.mutate()} loading={make.isPending}>Unterlagen erzeugen</Button>}
      >
        {make.isPending && <Text size="sm" c="dimmed">LibreOffice rechnet die Kalkulation nach und erzeugt die PDFs – das dauert je Variante etwa zehn Sekunden.</Text>}
        {make.isError && <Alert color="red">{make.error instanceof ApiError ? make.error.message : 'Die Erzeugung ist fehlgeschlagen.'}</Alert>}
        {list.isPending && <Center h={60}><Loader size="sm" /></Center>}
        {latest && <PackageReport offerId={offer.id} pkg={latest} />}
        {list.data && list.data.length === 0 && !make.isPending && <Text size="sm" c="dimmed">Noch keine Unterlagen erzeugt.</Text>}
      </Section>
      {list.data && list.data.length > 1 && (
        <Section title="Frühere Pakete" description="Die letzten fünf bleiben erhalten.">
          <Table verticalSpacing={3}>
            <Table.Tbody>
              {list.data.slice(1).map((p) => (
                <Table.Tr key={p.id}>
                  <Table.Td><Text size="xs">{formatDateTime(p.createdAt)}</Text></Table.Td>
                  <Table.Td><Anchor size="sm" href={offerFilesApi.url(offer.id, p.id)}>{p.fileName}</Anchor></Table.Td>
                  <Table.Td><Text size="xs" c="dimmed">{formatBytes(p.bytes)}</Text></Table.Td>
                  <Table.Td>{p.report.ok ? <Badge size="xs" color="green" variant="light">Prüfungen bestanden</Badge> : <Badge size="xs" color="orange" variant="light">mit Befunden</Badge>}</Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Section>
      )}
    </Stack>
  );
}

function PackageReport({ offerId, pkg }: { offerId: string; pkg: OfferPackage }): JSX.Element {
  const checks = pkg.report.checks ?? [];
  const open = Object.entries(pkg.report.open ?? {}).filter(([, list]) => list.length > 0);
  const files = (pkg.report.files ?? []).map((f) => f.split('/').slice(1).join(' / '));
  return (
    <Stack gap="sm">
      <Group gap="sm">
        <Button component="a" href={offerFilesApi.url(offerId, pkg.id)} variant="light">Paket herunterladen ({formatBytes(pkg.bytes)})</Button>
        <Text size="xs" c="dimmed">{pkg.fileName} · {formatDateTime(pkg.createdAt)}{pkg.report.templates ? ` · Vorlagen Version ${pkg.report.templates.calculation} / ${pkg.report.templates.document}` : ''}</Text>
        {pkg.report.ok ? <Badge color="green" variant="light">alle Prüfungen bestanden</Badge> : <Badge color="orange" variant="light">Prüfungen mit Befunden</Badge>}
      </Group>
      <Table verticalSpacing={2}>
        <Table.Thead><Table.Tr><Table.Th w={30} /><Table.Th>Prüfung</Table.Th><Table.Th>Befund</Table.Th></Table.Tr></Table.Thead>
        <Table.Tbody>
          {checks.map((c, i) => (
            <Table.Tr key={i}>
              <Table.Td><Text c={c.ok ? 'green.7' : 'orange.7'} fw={700}>{c.ok ? '✓' : '!'}</Text></Table.Td>
              <Table.Td><Text size="sm">{c.name}</Text></Table.Td>
              <Table.Td><Text size="xs" c="dimmed">{c.detail}</Text></Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
      {open.length > 0 && (
        <Alert color="orange" py={6} title="Offene Felder im Angebot">
          {open.map(([code, list]) => <Text key={code} size="xs">Variante {code}: {list.join(', ')}</Text>)}
          <Text size="xs" mt={4}>Die Deckungssummen der Berufshaftpflicht bleiben offen, bis die Police vorliegt; alles andere unter „Angaben und Texte“ ergänzen und neu erzeugen.</Text>
        </Alert>
      )}
      <Stack gap={2}>
        <Text size="sm" fw={500}>Im Paket</Text>
        <List size="xs" spacing={0}>{files.map((f) => <List.Item key={f}>{f}</List.Item>)}</List>
        <Text size="xs" c="dimmed">Lebensläufe, Referenzen, Verpflichtungserklärungen und Nachunternehmervereinbarungen kommen hinzu, wenn sie unter „Angaben und Texte › Anlagen im Paket“ gewählt sind.</Text>
      </Stack>
    </Stack>
  );
}
